/**
 * PATH: apps/quote/src/hooks/useQuoteBuilderTradeMode.ts
 * FILE_ID: QB-HOOK-TRADEMODE-001
 * PURPOSE: Read-side hook that resolves the current organisation's
 *          `quote_builder_mode` (waterproofing | steel | custom).
 *          Used by the scope grid + Setup tab so the rendered column
 *          set reflects the tenant's trade.
 *
 * WHY: `apps/quote/` is the master Quote Hub for every tenant on
 *      `quote.quotech.ai`. Before this hook landed, the AG-Grid
 *      column builder hard-coded the steel-fabrication allowance
 *      bucket band on for every quote — waterproofers got 8 noise
 *      columns scrolling off the right of their grid (commit
 *      81823c119 "feat(quote-builder): allowance bucket columns").
 *      This hook is the bridge between the per-org backend setting
 *      and the per-quote grid layout.
 *
 * FLOW:
 *   ┌────────────────────┐    ┌────────────────────┐    ┌────────────────┐
 *   │ GET /api/          │ -> │ find org matching  │ -> │ tradeMode :    │
 *   │ organisations/     │    │ user.organisation  │    │ 'waterproofing'│
 *   │ (paginated list)   │    │ _id                │    │ | 'steel' | …  │
 *   └────────────────────┘    └────────────────────┘    └────────────────┘
 *
 * RELATED:
 *   - QB-LIB-TRADEMODE-001 — type union + parser + DEFAULT_TRADE_MODE
 *   - QB-AGGRID-COLUMNS-001 — consumer of `tradeMode` in BuildOptions
 *   - organisation.serializers.base.OrganisationSerializer — backend
 *     contract; field added in MR for `quote_builder_mode`.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@quotech/auth'

import { getApiClient } from '@/lib/setupApi'
import {
  DEFAULT_TRADE_MODE,
  parseQuoteBuilderTradeMode,
  type QuoteBuilderTradeMode,
} from '@/lib/trade-mode'

interface OrganisationListEntry {
  id: number | string
  quote_builder_mode?: string | null
}

interface OrganisationListResponse {
  results?: OrganisationListEntry[]
}

/**
 * Find the org row whose id matches the active user's organisation_id.
 *
 * I→O. Returns the parsed `quote_builder_mode` literal, or null when
 * the response is missing / malformed / not yet loaded. The caller is
 * responsible for choosing the fallback (we expose `DEFAULT_TRADE_MODE`
 * separately so callers don't accidentally render the steel band
 * during the brief initial-fetch window).
 */
export function findTradeModeForOrg(
  orgs: OrganisationListEntry[] | undefined,
  organisationId: string | number | null | undefined,
): QuoteBuilderTradeMode | null {
  if (!orgs || organisationId == null) return null
  const wanted = String(organisationId)
  const match = orgs.find((o) => String(o.id) === wanted)
  return parseQuoteBuilderTradeMode(match?.quote_builder_mode)
}

interface UseTradeModeResult {
  /** Resolved trade mode for the active org, or null if unknown. */
  tradeMode: QuoteBuilderTradeMode | null
  /**
   * Convenience accessor: same as `tradeMode` but never null. Falls
   * back to `DEFAULT_TRADE_MODE` ('waterproofing') during the initial
   * fetch and when the org row is missing. This is what the column
   * gate consumes so first paint doesn't flash the steel band for
   * waterproofers.
   */
  effectiveTradeMode: QuoteBuilderTradeMode
  isLoading: boolean
  error: unknown
}

/**
 * Resolve the active organisation's Quote Hub trade mode.
 *
 * Backed by TanStack Query so the network call deduplicates across
 * concurrent consumers (the Setup tab selector AND the scope grid
 * both want the same value on every mount of `/builder/:id`).
 */
export function useQuoteBuilderTradeMode(): UseTradeModeResult {
  const { user } = useAuth()
  const organisationId = user?.organisation_id ?? null

  const query = useQuery({
    queryKey: ['quote-builder-trade-mode', 'org-list'],
    queryFn: async (): Promise<OrganisationListEntry[]> => {
      const apiClient = getApiClient()
      const { data } = await apiClient.get<OrganisationListResponse | OrganisationListEntry[]>(
        '/organisations/',
      )
      if (Array.isArray(data)) return data
      return data?.results ?? []
    },
    // Trade mode changes maybe once in an org's lifetime — cache long.
    staleTime: 5 * 60 * 1000,
    // Don't refetch on window focus; the value is effectively static.
    refetchOnWindowFocus: false,
    enabled: !!organisationId,
  })

  const resolvedMode = useMemo(
    () => findTradeModeForOrg(query.data, organisationId),
    [query.data, organisationId],
  )

  return {
    tradeMode: resolvedMode,
    effectiveTradeMode: resolvedMode ?? DEFAULT_TRADE_MODE,
    isLoading: query.isLoading,
    error: query.error,
  }
}
